import { QueryCache, QueryClient, MutationCache } from '@tanstack/react-query';
import { ApiError } from '@/lib/api/api-error';
import { useSessionStore } from '@/stores/session-store';

const RETRYABLE_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);

function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 3) {
    return false;
  }

  if (error instanceof ApiError) {
    return error.status === null || RETRYABLE_STATUS.has(error.status);
  }

  return true;
}

function onUnauthenticated(error: unknown): void {
  if (error instanceof ApiError && error.status === 401) {
    useSessionStore.getState().clearSession();
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 30 * 60_000,
      retry: shouldRetry,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 15_000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      networkMode: 'offlineFirst',
      throwOnError: false,
    },
    mutations: {
      retry: (failureCount, error) =>
        error instanceof ApiError && error.status === null && failureCount < 2,
      networkMode: 'offlineFirst',
    },
  },
  queryCache: new QueryCache({ onError: onUnauthenticated }),
  mutationCache: new MutationCache({ onError: onUnauthenticated }),
});
